import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ReduxSelectorProps } from '../../services/constants';
import { ComponentConfig, DropZone } from '@measured/puck';
import { borderProps } from '../../puck/reusable-props/Border';
import { marginProps } from '../../puck/reusable-props/margin';
import { paddingProps } from '../../puck/reusable-props/padding';
import CustomTooltip from '../../../components/common/tooltip';
import AccordionHeader, { HeaderProps } from './fields/header';
import { Body, BodyProps } from './fields/Body';
import Common, { CommonProps } from './fields/Common';
import { CartTotal } from '../cart/cart-total/CartTotal';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import { Path } from '../../utils/constants';
import { useLocation } from 'react-router';
import '../text-editor/style.scss';

export type IAccordionProps = {
  header: HeaderProps;
  container: BodyProps;
  common: CommonProps;
};

const Accordion = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<IAccordionProps> => {
  return {
    label: (
      <CustomTooltip title="Accordion">
        <span>Accordion</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      common: {
        type: 'custom',
        render: ({ value, onChange }) => {
          return <Common value={value} onChange={onChange} />;
        },
      },
      header: {
        type: 'custom',
        render: ({ value, onChange }) => {
          return <AccordionHeader value={value} onChange={onChange} />;
        },
      },
      container: {
        type: 'custom',
        render: ({ value, onChange }) => {
          return <Body value={value} onChange={onChange} />;
        },
      },
    },
    defaultProps: {
      common: {
        background: {
          color: '#ffffff',
          isInherit: true,
        },
        border: borderProps?.border,
        margin: marginProps?.margin,
        padding: paddingProps?.padding,
        height: 'auto',
        position: 'none',
      },
      header: {
        background: {
          color: '#ffffff',
          isInherit: true,
        },
        iconPlacement: 'left',
        width: {
          deviceType: 'desktop',
          width: {
            desktop: '100%',
            tablet: '100%',
            mobile: '100%',
          },
        },
        maxWidth: {
          deviceType: 'desktop',
          width: {
            desktop: 'none',
            tablet: 'none',
            mobile: 'none',
          },
        },
        justifyContent: {
          currentDevice: 'desktop',
          justify: {
            desktop: 'justify-start',
            tablet: 'justify-start',
            mobile: 'justify-start',
          },
        },
        border: borderProps?.border,
        margin: marginProps?.margin,
        padding: paddingProps?.padding,
        orderAmount: {
          show: true,
          color: '#000000',
          fontSize: 'text-md',
          fontWeight: 'font-bold',
        },
        text: '<h3 style="line-height:1.5;"><span style="font-size: 16px;">Show order summary</span></h3>',
        collapse: {
          currentDevice: 'tablet',
          showAccordion: {
            mobile: {
              show: true,
              defaultCollapsed: true,
            },
            tablet: {
              show: true,
              defaultCollapsed: true,
            },
            desktop: {
              show: false,
              defaultCollapsed: false,
            },
          },
        },
        icon: {
          show: true,
          color: '#000000',
          textColor: '#000000',
          width: '16px',
          textShow: true,
          height: 'h-6',
        },
      },
      container: {
        showComponent: ['desktop', 'tablet', 'mobile'],
        background: {
          color: '#ffffff',
          isInherit: true,
        },
        width: {
          deviceType: 'desktop',
          width: {
            desktop: '100%',
            tablet: '100%',
            mobile: '100%',
          },
        },
        maxWidth: {
          deviceType: 'desktop',
          width: {
            desktop: 'none',
            tablet: 'none',
            mobile: 'none',
          },
        },
        justifyContent: {
          currentDevice: 'desktop',
          justify: {
            desktop: 'justify-start',
            tablet: 'justify-start',
            mobile: 'justify-start',
          },
        },
        border: borderProps?.border,
        margin: marginProps?.margin,
        padding: paddingProps?.padding,
        customize: {
          flexDirection: 'row',
          gap: 'gap-2',
          height: 'auto',
          boxShadow: 'none',
          verticalAlign: 'justify-stretch',
        },
      },
    },
    render: ({ header, container, common, editMode }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const { pathname } = useLocation();
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const state = useAppSelector(state => state);
      const newHeader = {
        ...header,
        text: getQuillEditorText(header?.text, state),
      };
      const [dropDown, setDropDown] = useState(
        !newHeader?.collapse?.showAccordion[currentDeviceType]
          ?.defaultCollapsed,
      );
      const observerRef = useRef<MutationObserver | null>(null);
      const [showAccordion, setShowAccordion] = useState(
        newHeader?.collapse?.showAccordion[currentDeviceType]?.show,
      );
      const toggleDropZone = () => setDropDown(!dropDown);

      let headerWidth: string;
      if (newHeader?.width?.width[currentDeviceType]?.includes('%')) {
        headerWidth = newHeader?.width?.width?.[currentDeviceType];
      } else {
        headerWidth = `${newHeader?.width?.width?.[currentDeviceType]}px`;
      }
      let containerWidth: string;
      if (container?.width?.width?.[currentDeviceType]?.includes('%')) {
        containerWidth = container?.width?.width?.[currentDeviceType];
      } else {
        containerWidth = `${container?.width?.width?.[currentDeviceType]}px`;
      }

      let maxContainerWidth: string;
      if (container?.maxWidth?.width?.[currentDeviceType]?.includes('%')) {
        maxContainerWidth = container?.maxWidth?.width?.[currentDeviceType];
      } else if (container?.maxWidth?.width?.[currentDeviceType]) {
        maxContainerWidth = `${container?.maxWidth?.width?.[currentDeviceType]}px`;
      } else {
        maxContainerWidth = 'none';
      }
      let maxHeaderWidth: string;
      if (newHeader?.maxWidth?.width?.[currentDeviceType]?.includes('%')) {
        maxHeaderWidth = newHeader?.maxWidth?.width?.[currentDeviceType];
      } else if (newHeader?.maxWidth?.width?.[currentDeviceType]) {
        maxHeaderWidth = `${newHeader?.maxWidth?.width?.[currentDeviceType]}px`;
      } else {
        maxHeaderWidth = 'none';
      }

      useEffect(() => {
        setDropDown(
          !newHeader?.collapse?.showAccordion[currentDeviceType]
            ?.defaultCollapsed,
        );
        setShowAccordion(
          newHeader?.collapse?.showAccordion[currentDeviceType]?.show,
        );
      }, [currentDeviceType]);

      useEffect(() => {
        const iframe = document.getElementById(
          'preview-frame',
        ) as HTMLIFrameElement;
        const checkScroll = () => {
          if (iframe?.contentDocument) {
            const hasScrollbar =
              iframe?.contentDocument.documentElement.scrollHeight >
              iframe?.contentDocument.documentElement.clientHeight;
            const iframeDocument =
              iframe.contentDocument || iframe.contentWindow?.document;
            const dropzoneHeight =
              iframeDocument?.querySelector('.dropzone-height');
            const frameRootDiv = iframeDocument?.getElementById('frame-root');

            if (hasScrollbar && dropzoneHeight) {
              (dropzoneHeight as HTMLElement).style.removeProperty('height');
            } else if (frameRootDiv && dropzoneHeight) {
              (dropzoneHeight as HTMLElement).style.height = '100vh';
            }
          }
        };
        observerRef.current = new MutationObserver(checkScroll);
        observerRef.current.observe(document.body, {
          childList: true,
          subtree: true,
        });
        window.addEventListener('resize', checkScroll);

        setTimeout(() => {
          if (iframe?.contentDocument) {
            const childDivs = iframe?.contentDocument?.querySelectorAll(
              "div[data-puck-component*='Accordion']",
            ) as NodeListOf<HTMLElement>;

            childDivs.forEach(childDiv => {
              if (childDiv) {
                if (isNaN(+common?.height)) {
                  childDiv.style.height = common?.height;
                } else {
                  childDiv.style.removeProperty('common?.height');
                }
              }
            });
          }
        }, 100);
      }, [common?.height]);

      return (
        <div
          className={`text-editor-content`}
          style={{
            backgroundColor: common?.background?.isInherit
              ? 'inherit'
              : common?.background?.color,
            ...getMarginStyles(common?.margin, currentDeviceType),
            ...getBorderProps(common?.border, currentDeviceType, true),
            ...getPaddingStyles(common?.padding, currentDeviceType),
            height: editMode
              ? isNaN(+common?.height)
                ? common?.height === '100%'
                  ? '100%'
                  : '100%'
                : +common?.height
              : common?.height,
          }}>
          <div className={common?.position}>
            {newHeader?.collapse?.showAccordion?.[currentDeviceType]?.show && (
              <div
                className={`flex ${newHeader?.justifyContent?.justify?.[currentDeviceType]}`}>
                <div
                  className={`flex justify-between`}
                  onClick={toggleDropZone}
                  style={{
                    backgroundColor: newHeader?.background?.isInherit
                      ? 'inherit'
                      : newHeader?.background?.color,
                    width: headerWidth,
                    maxWidth: maxHeaderWidth,
                    ...getMarginStyles(newHeader?.margin, currentDeviceType),
                    ...getBorderProps(newHeader?.border, currentDeviceType),
                    ...getPaddingStyles(newHeader?.padding, currentDeviceType),
                  }}>
                  <button type="button" className={`flex items-center`}>
                    <div
                      className="flex"
                      style={{ lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: newHeader?.text }}
                    />
                    {newHeader?.icon?.show &&
                      newHeader.iconPlacement !== 'right' && (
                        <ChevronDownIcon
                          className={`transition-transform duration-200 ${dropDown ? 'transform rotate-180' : ''} ${newHeader?.icon?.width} ${newHeader?.icon?.height}`}
                          style={{
                            color: newHeader?.icon?.color,
                          }}
                          aria-hidden="true"
                        />
                      )}
                  </button>
                  <div className="flex items-center">
                    {newHeader?.orderAmount?.show && (
                      <CartTotal
                        className={`${newHeader?.orderAmount?.fontSize} ${newHeader?.orderAmount?.fontWeight}`}
                        style={{
                          color: newHeader?.orderAmount?.color,
                        }}
                        useAppSelector={useAppSelector}
                      />
                    )}
                    {newHeader?.icon?.show &&
                      newHeader.iconPlacement === 'right' && (
                        <div className="flex items-center pl-2">
                          {newHeader.icon.textShow && (
                            <div style={{ color: newHeader?.icon?.textColor }}>
                              {dropDown ? 'Hide' : 'Show'}
                            </div>
                          )}
                          <ChevronDownIcon
                            className={`transition-transform duration-200 ${dropDown ? 'transform rotate-180' : ''} ${newHeader?.icon?.width} ${newHeader?.icon?.height}`}
                            style={{ color: newHeader?.icon?.color }}
                            aria-hidden="true"
                          />
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}
            {(dropDown ||
              !showAccordion ||
              pathname.includes(Path.PAGE_BUILDER_EDIT) ||
              pathname.includes(Path.PAGE_BUILDER_TEMPLATE_EDIT)) && (
              <div
                className={`Container flex ${container?.customize?.gap}  ${container?.customize?.boxShadow} ${container?.border?.borderStyle?.[currentDeviceType]} ${container?.showComponent?.includes(currentDeviceType) ? '' : '!hidden'} ${container?.justifyContent?.justify?.[currentDeviceType]}`}
                style={{
                  flexDirection:
                    (container?.customize
                      ?.flexDirection as React.CSSProperties['flexDirection']) ||
                    'row',
                  ...container?.margin,
                  height: isNaN(+container?.customize?.height)
                    ? container?.customize?.height === '100%'
                      ? '100%'
                      : '100%'
                    : +container?.customize?.height,
                }}>
                <div
                  className={`flex w-full h-full ${container?.customize?.verticalAlign} flex-col`}
                  style={{
                    width: containerWidth,
                    maxWidth: maxContainerWidth,
                    ...getPaddingStyles(container?.padding, currentDeviceType),
                    ...getBorderProps(container?.border, currentDeviceType),
                    backgroundColor: container?.background?.isInherit
                      ? 'inherit'
                      : container?.background?.color,
                    ...container?.margin,
                    height: isNaN(+container?.customize?.height)
                      ? container?.customize?.height === '100%'
                        ? '100%'
                        : '100%'
                      : +container?.customize?.height,
                    ...getPaddingStyles(container?.padding, currentDeviceType),
                  }}>
                  <DropZone zone={`Container`} />
                </div>
              </div>
            )}
          </div>
        </div>
      );
    },
  };
};

export default Accordion;
